/**
 * Site Intelligence App Component
 *
 * AI-powered document Q&A for clinical sites.
 * Integrated into SiteOperations.io - uses shared authentication.
 * Note: IntelligenceProvider is now in App.tsx to persist state across navigation.
 */

import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useIntelligence } from './contexts';
import { Layout } from './components/layout';
import { Chat, History, Documents, Protocols, ProtocolDetail, Users, Settings, Oversight, RegistryAdoption } from './pages';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

// Protected route wrapper - uses shared auth from main app
function ProtectedRoute({ children, requireAdmin = false, requireSuperAdmin = false }: {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireSuperAdmin?: boolean;
}) {
  const { session, user: authUser, currentSite } = useAuth();
  const { user, loading, isAdmin } = useIntelligence();
  const navigate = useNavigate();
  const [entitlementChecked, setEntitlementChecked] = useState(false);
  const [hasEntitlement, setHasEntitlement] = useState(false);
  const [checkingEntitlement, setCheckingEntitlement] = useState(true);

  // Check entitlement when component mounts or site changes
  useEffect(() => {
    const checkEntitlement = async () => {
      // Super admins always have access
      if (authUser?.role === 'super_admin') {
        setHasEntitlement(true);
        setEntitlementChecked(true);
        setCheckingEntitlement(false);
        return;
      }

      if (!currentSite) {
        // No site selected = no access to tools
        setHasEntitlement(false);
        setEntitlementChecked(true);
        setCheckingEntitlement(false);
        return;
      }

      try {
        const response = await api.get('/api/my-tools');
        const tools = response.data || [];
        const intelligenceTool = tools.find((t: any) => t.tool_id === 'site_intelligence');
        setHasEntitlement(intelligenceTool?.is_available === true);
        setEntitlementChecked(true);
      } catch (error) {
        console.error('Error checking intelligence entitlement:', error);
        setHasEntitlement(false);
        setEntitlementChecked(true);
      } finally {
        setCheckingEntitlement(false);
      }
    };

    if (session && !loading) {
      checkEntitlement();
    }
  }, [session, authUser?.role, currentSite, loading]);

  if (loading || checkingEntitlement) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  // If no session, redirect to main login
  if (!session) {
    return <Navigate to="/login" replace />;
  }

  // Check entitlement (super admins bypass this check)
  if (entitlementChecked && !hasEntitlement && authUser?.role !== 'super_admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center p-8 max-w-md">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2 dark:text-gray-100">Access Denied</h1>
          <p className="text-gray-600 mb-6 dark:text-gray-400">
            You do not have access to Site Intelligence for your current site.
            Please contact your administrator if you believe this is an error.
          </p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0"
          >
            Return to Tool Hub
          </button>
        </div>
      </div>
    );
  }

  // If super admin required but user is not super admin
  if (requireSuperAdmin && authUser?.role !== 'super_admin') {
    return <Navigate to="/intelligence/chat" replace />;
  }

  // If admin required but user is not admin
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/intelligence/chat" replace />;
  }

  return <>{children}</>;
}

// Intelligence Routes
function IntelligenceRoutes() {
  return (
    <Routes>
      {/* Default route goes to chat */}
      <Route path="/" element={<Navigate to="/intelligence/chat" replace />} />

      {/* Main chat interface */}
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <Layout>
              <Chat />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Chat history */}
      <Route
        path="/history"
        element={
          <ProtectedRoute>
            <Layout>
              <History />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Document management - all users with entitlement */}
      <Route
        path="/documents"
        element={
          <ProtectedRoute>
            <Layout>
              <Documents />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* The Upload Queue moved to the eReg unified intake (P5 §8-P5). */}
      <Route path="/staging" element={<Navigate to="/ereg/intake" replace />} />

      {/* Protocol/Study folders - all users with entitlement */}
      <Route
        path="/protocols"
        element={
          <ProtectedRoute>
            <Layout>
              <Protocols />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Protocol detail with documents - all users with entitlement */}
      <Route
        path="/protocols/:protocolId"
        element={
          <ProtectedRoute>
            <Layout>
              <ProtocolDetail />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Admin: User access management */}
      <Route
        path="/users"
        element={
          <ProtectedRoute requireAdmin>
            <Layout>
              <Users />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Settings */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <Settings />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Super Admin: Chat Oversight */}
      <Route
        path="/oversight"
        element={
          <ProtectedRoute requireSuperAdmin>
            <Layout>
              <Oversight />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Super Admin: one-time eReg registry adoption (plan §7.5) */}
      <Route
        path="/registry-adoption"
        element={
          <ProtectedRoute requireSuperAdmin>
            <Layout>
              <RegistryAdoption />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to chat */}
      <Route path="*" element={<Navigate to="/intelligence/chat" replace />} />
    </Routes>
  );
}

// Main Intelligence App
// Note: Provider is now in App.tsx to persist state across navigation
export function IntelligenceApp() {
  return <IntelligenceRoutes />;
}

export default IntelligenceApp;
